/**
 * Shared error-copy dictionary for insights surfaces (Reports dashboard + the
 * experiment form's MDE FeasibilityPanel). Pure and React-free (same
 * import-safety idiom as testIds.ts) so Playwright specs can import it.
 *
 * Two distinct failure states (ux-rework-plan.md R0-1 / handoff B1):
 * - transient  — viz data not materialized YET on a refresh-capable dataset;
 * - structural — the project's data scope can never materialize dashboards
 *   (mirror / insights-only projects). Never promise materialization here.
 *
 * Copy anatomy follows the Launch-readiness pattern (handoff B9):
 * state + why + CTA; raw BigQuery text only ever appears in techDetails.
 */
import type { FunnelReportDataSectionId } from '../reportAvailability';
import type { FunnelReportSectionError } from '../types';

export type InsightsErrorSeverity = 'info' | 'warning' | 'error';

export interface InsightsErrorCopy {
  headline: string;
  severity: InsightsErrorSeverity;
  /** Raw upstream text (BigQuery message etc.) — collapsible tech row only. */
  techDetails?: string;
}

export const TECH_DETAILS_LABEL = 'Technical details';

export const FUNNEL_REPORT_SECTION_LABELS: Record<FunnelReportDataSectionId, string> = {
  'active-placements': 'Active placements',
  'funnel-health': 'Funnel health',
  'metric-timeseries': 'Metric trends',
  'screen-flow': 'Screen flow',
  payments: 'Payments',
  'anomaly-inbox': 'Anomaly inbox',
  'root-cause': 'Root cause',
  artifacts: 'Creative & screen artifacts',
  'data-quality': 'Data quality',
  'paid-acquisition': 'Paid acquisition',
};

const STRUCTURAL_HEADLINE = "Dashboard reports are not available for this project's data scope.";

/**
 * Maps any thrown insights-client error to operator copy (Layer 1).
 * Typed codes only — PE-1 landed, so the backend always returns
 * `sectionErrors[].code` / problem+json codes (contract: ux-rework-handoff.md
 * §A8); the former raw-BQ message-matching safety net is gone. Reads
 * `code`/`detail` structurally (InsightsApiError shape) instead of via
 * instanceof, so this module never runtime-imports the client (keeps it safe
 * for jest module mocks and Playwright node imports).
 */
export function describeInsightsError(error: unknown): InsightsErrorCopy {
  const message = error instanceof Error ? error.message : String(error);
  const shaped = error as { code?: unknown; detail?: unknown };
  const code = typeof shaped?.code === 'string' ? shaped.code : undefined;
  const detail = typeof shaped?.detail === 'string' ? shaped.detail : undefined;
  if (code === 'not_materialized') {
    return {
      headline: 'Report data is not materialized yet for this window.',
      severity: 'warning',
      techDetails: detail,
    };
  }
  if (code === 'source_unavailable_for_scope') {
    return {
      headline: STRUCTURAL_HEADLINE,
      severity: 'info',
      techDetails: detail,
    };
  }
  return { headline: message, severity: 'error' };
}

/** Per-section copy for the PE-1 `sectionErrors[]` contract (Layer 2). */
export function describeReportSectionError(sectionError: FunnelReportSectionError): InsightsErrorCopy {
  const label = FUNNEL_REPORT_SECTION_LABELS[sectionError.section] ?? sectionError.section;
  switch (sectionError.code) {
    case 'not_materialized':
      return {
        headline: `Report data for ${label} is not materialized yet for this window.`,
        severity: 'warning',
        techDetails: sectionError.detail,
      };
    case 'source_unavailable_for_scope':
      return {
        headline: STRUCTURAL_HEADLINE,
        severity: 'info',
        techDetails: sectionError.detail,
      };
    // Unknown future codes degrade to the generic failure copy (forward-compatible).
    case 'query_failed':
    default:
      return {
        headline: `${label} could not be loaded for this window.`,
        severity: 'error',
        techDetails: sectionError.detail,
      };
  }
}

/**
 * Page-level rollup for the all-sections-failed banner.
 * Dominance: structural > transient > failure.
 */
export function describeReportSectionErrors(sectionErrors: FunnelReportSectionError[]): InsightsErrorCopy {
  if (sectionErrors.some(entry => entry.code === 'source_unavailable_for_scope')) {
    return { headline: STRUCTURAL_HEADLINE, severity: 'info' };
  }
  if (sectionErrors.some(entry => entry.code === 'not_materialized')) {
    return {
      headline: 'Report data is not materialized yet for this window.',
      severity: 'warning',
    };
  }
  return {
    headline: 'Report sections could not be loaded for this window.',
    severity: 'error',
  };
}
